export class Revision {
  constructor({ max = 15, room = null } = {}) {
    this.max = max + 1;
    this.undos = [];
    this.redos = [];
    this.room = room;
  }

  get undoable() {
    return this.undos.length > 0;
  }

  get redoable() {
    return this.redos.length > 0;
  }

  async commit(transaction, n) {
    this.undos.push(transaction);

    if (this.undos.length > this.max) {
      this.undos.shift();
    }

    this.redos = [];

    await transaction.commit(n);
    this.broadcast(transaction);
  }

  async commitNotUndoable(transaction, n) {
    await transaction.commit(n);
    this.broadcast(transaction);
  }

  // Peers only learn about a write from this broadcast; without it their copy
  // of the record stays stale until something unrelated forces a refetch, and
  // their next write overwrites what was persisted here.
  broadcast(transaction) {
    if (!this.room || !transaction.updated?.length) return;

    this.room.send({
      type: "broadcast",
      event: "update",
      payload: {
        updated: transaction.updated,
        updated_at: new Date().toISOString(),
      },
    });
  }

  // undo and redo persist real writes — the transaction closures call
  // updateProp — so they have to tell peers for the same reason commit does.
  //
  // Neither awaits its write: the closures are synchronous and drop the
  // promise updateProp returns, so unlike commit() we cannot order the
  // broadcast after the round trip. A peer can therefore refetch fractionally
  // early and see the pre-write row. That is still strictly better than never
  // broadcasting, which left the peer stale indefinitely and let its next
  // write overwrite this one. Ordering it properly means making both methods
  // async, which their callers in stores/group.js cannot await from inside a
  // Svelte `update()` callback.
  undo(n) {
    if (!this.undoable) return;
    const transaction = this.undos.pop();
    transaction.isUndo = true;
    transaction.undo(n);
    this.redos.push(transaction);
    this.broadcast(transaction);
  }

  redo(n) {
    if (!this.redoable) return;
    const transaction = this.redos.pop();
    delete transaction.isUndo;
    transaction.commit(n);
    this.undos.push(transaction);
    this.broadcast(transaction);
  }

  async commitTransaction(commit, n, updated = []) {
    const commitId = crypto.randomUUID();

    const transaction = {
      commitId,
      updated,
      async commit(n) {
        this.undo = await commit(n);
      },
    };

    await this.commit(transaction, n);
  }

  async commitTransactionNotUndoable(commit, n, updated) {
    const commitId = crypto.randomUUID();

    const transaction = {
      commitId,
      updated,
      async commit(n) {
        await commit(n);
      },
    };

    await this.commitNotUndoable(transaction, n);
  }
}
